import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';

const PublishMQTTSetting = () => {

  const { id, settingtype } = useParams();
  
  const [device, setDevice] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState('Not connected');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('No file selected');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [jsonConfig, setJsonConfig] = useState(null);
  const [isConnectDisabled, setIsConnectDisabled] = useState(false);

  const m_report_len = 65;
  let m_gNumber = 0x01;

  const getConfigSettings = () => {
    switch (settingtype?.toLowerCase()) {
      case 'screen setting':
        return {
          title: 'Upload Screen Configuration',
          fileName: 'theme.json',
          fileExtension: '.json',
          hidCommand: [0x03, 0x1E],
          defaultConfig: {
            theme: "default",
            background_color: "#ffffff",
            text_color: "#000000",
            font_size: 16
          }
        };
      case 'master setting':
        return {
          title: 'Upload Master Configuration',
          fileName: 'master_url.json',
          fileExtension: '.json',
          hidCommand: [0x03, 0x1D],
          defaultConfig: "https://api.master.example.com/endpoint"
        };
         case 'wifi setting':
        return {
          title: 'Upload wifi Configuration',
          fileName: 'wifi_conf.json',
          fileExtension: '.json',
          hidCommand: [0x03, 0x1F],
          defaultConfig: "https://api.master.example.com/endpoint"
        };
      case 'MQTT setting':
      default:
        return {
          title: 'Upload MQTT Configuration',
          fileName: 'mqtt_conf.json',
          fileExtension: '.json',
          hidCommand: [0x03, 0x17],
          defaultConfig: {
            broker: "mqtt.broker.example.com",
            port: 1883,
            client_id: "device_001",
            username: "mqtt_user",
            password: "mqtt_password"
          }
        };
    }
  };

  const configSettings = getConfigSettings();

  useEffect(() => {
    if (!('hid' in navigator)) {
      log('WebHID is not supported in this browser');
      setIsConnectDisabled(true);
    }
    if (id) {
      fetchJsonConfig(id);
    }
  }, [id]);

  const fetchJsonConfig = async (configId) => {
    try {
      log(`Fetching ${settingtype || 'MQTT'} configuration for ID: ${configId}`);
      const response = await fetch(`https://api.dqr-display-tms.eventum.co.in/api/Share/JsonSetting/Json/${configId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setJsonConfig(data);
      if(data.errormsg == 'No matching records found'){
      
          window.location.href = "/not-found";
      }
      log(`Configuration fetched successfully`);
      
      const fileName = `config-${configId}.${configSettings.fileExtension.replace('.', '')}`;
      let fileContent;
      let contentType;
      
      if (settingtype?.toLowerCase() === 'master') {
        fileContent = typeof data === 'string' ? data : JSON.stringify(data);
        contentType = 'text/plain';
      } else {
        fileContent = JSON.stringify(data, null, 2);
        contentType = 'application/json';
      }
      
      setFileName(fileName);
      setSelectedFile(new Blob([fileContent], { type: contentType }));
     
      
    } catch (error) {
      log(`Error fetching configuration: ${error.message}`);
    }
  };

  const log = (message) => {
    setLogs(prev => [...prev, message]);
  };

  const connectDevice = async () => {
    try {
      const devices = await navigator.hid.requestDevice({
        filters: [{ vendorId: 0x2C7C, productId: 0x0901 }]
      });

      if (devices.length === 0) {
        log('No device selected');
        return;
      }

      const selectedDevice = devices[0];
      await selectedDevice.open();

      setDevice(selectedDevice);
      setDeviceStatus(`Connected: ${selectedDevice.productName}`);
      log(`Connected to ${selectedDevice.productName}`);
      setIsConnectDisabled(true);
    } catch (error) {
      log(`Error connecting to device: ${error.message}`);
    }
  };

  const downloadConfig = () => {
    try {
      const configToUse = jsonConfig || configSettings.defaultConfig;
      let fileContent;
      let contentType;
      let fileName = configSettings.fileName;

      if (settingtype?.toLowerCase() === 'master') {
        fileContent = typeof configToUse === 'string' ? configToUse : JSON.stringify(configToUse);
        contentType = 'text/plain';
      } else {
        fileContent = JSON.stringify(configToUse, null, 2);
        contentType = 'application/json';
      }

      const blob = new Blob([fileContent], { type: contentType });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      log(`${settingtype || 'MQTT'} configuration file downloaded`);
    } catch (error) {
      log(`Error downloading configuration: ${error.message}`);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files.length) {
      const file = e.target.files[0];
      const expectedExtension = configSettings.fileExtension;

      if (!file.name.toLowerCase().endsWith(expectedExtension)) {
        alert(`Please select a ${expectedExtension} file only!`);
        e.target.value = '';
        setFileName('No file selected');
        setSelectedFile(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = function (event) {
        try {
          let configData = event.target.result;

          if (expectedExtension === '.json') {
            const parsedData = JSON.parse(configData);
            log(`Valid ${settingtype || 'MQTT'} configuration JSON selected: ${file.name}`);
          } else if (expectedExtension === '.json') {
            log(`Valid ${settingtype || 'Master'} configuration text file selected: ${file.name}`);
          }

          setFileName(file.name);
          setSelectedFile(file);
        } catch (error) {
          alert(`Invalid ${expectedExtension} file! Please select a valid ${expectedExtension} file.`);
          e.target.value = '';
          setFileName('No file selected');
          setSelectedFile(null);
          log(`Invalid ${expectedExtension} file: ${error.message}`);
        }
      };

      reader.readAsText(file);
    } else {
      setFileName('No file selected');
      setSelectedFile(null);
    }
  };

  const uploadConfiguration = async () => {
    if (!device) {
      log('Please connect to a device first');
      return;
    }

    let fileToUpload;
    if (jsonConfig) {
      let fileContent;
      let contentType;
      
      if (settingtype?.toLowerCase() === 'master') {
        fileContent = typeof jsonConfig === 'string' ? jsonConfig : JSON.stringify(jsonConfig);
        contentType = 'text/plain';
      } else {
        fileContent = JSON.stringify(jsonConfig, null, 2);
        contentType = 'application/json';
      }
      
      fileToUpload = new Blob([fileContent], { type: contentType });
    } else {
      fileToUpload = selectedFile;
    }

    if (!fileToUpload) {
      log(`Please select a ${configSettings.fileExtension} file or provide a valid configuration ID`);
      return;
    }

    setIsUploading(true);
    setProgress(0);

    try {
      let content;
      if (jsonConfig) {
        if (settingtype?.toLowerCase() === 'master') {
          content = typeof jsonConfig === 'string' ? jsonConfig : JSON.stringify(jsonConfig);
        } else {
          content = JSON.stringify(jsonConfig, null, 2);
        }
      } else {
        content = await fileToUpload.text();
      }
      
      log('Content Preview:');
      log(content.substring(0, 200) + (content.length > 200 ? '...' : ''));

      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        setProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      log('Configuration uploaded successfully!');
      log('Configuration saved and device ready for use.');
      
    } catch (error) {
      log(`Upload error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  
  return (
    <div style={{
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      margin: 0,
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffffee',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
        borderRadius: '15px',
        width: '450px',
        maxWidth: '90vw',
        padding: '35px',
        textAlign: 'center',
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <h2 style={{
          marginBottom: '25px',
          color: '#2c3e50',
          fontWeight: '700',
          letterSpacing: '1px'
        }}>
          {configSettings.title}
        </h2>

        {settingtype && (
          <div style={{ 
            marginBottom: '15px', 
            padding: '8px 12px', 
            backgroundColor: '#e8f5e8', 
            borderRadius: '5px',
            fontSize: '14px',
            color: '#2e7d32',
            fontWeight: '600'
          }}>
            Setting Type: {settingtype.toUpperCase()}
          </div>
        )}

        <button
          onClick={connectDevice}
          disabled={isConnectDisabled}
          style={{
            backgroundColor: isConnectDisabled ? '#ccc' : '#848b92',
            color: 'white',
            fontSize: '16px',
            padding: '12px 25px',
            border: 'none',
            borderRadius: '8px',
            cursor: isConnectDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            fontWeight: '600'
          }}
        >
          Connect Device
        </button>

        <br /><br />

        <span style={{
          fontSize: '14px',
          color: '#999',
          display: 'block',
          marginBottom: '20px'
        }}>
          {deviceStatus}
        </span>

        {/* {id && (
          <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
            <small style={{ color: '#1976d2' }}>
              Configuration ID: {id} {jsonConfig ? '✓ Loaded' : '⏳ Loading...'}
            </small>
          </div>
        )} */}

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '15px',
          marginTop: '20px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={downloadConfig}
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              fontSize: '14px',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(23, 162, 184, 0.2)',
              fontWeight: '600'
            }}
          >
            Download Config
          </button>
        </div>

        <div style={{
          margin: '25px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px'
        }}>
          <input
            type="file"
            accept={configSettings.fileExtension}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="fileInput"
          />
          
          
          
          {/* <span style={{
            fontSize: '15px',
            color: '#555',
            padding: '8px 16px',
            border: '1px dashed #ccc',
            borderRadius: '6px',
            minHeight: '24px',
            transition: 'all 0.3s',
            backgroundColor: '#f8f9fa',
            width: '100%',
            maxWidth: '300px'
          }}>
            {fileName}
          </span> */}
        </div>

        <div style={{
          width: '100%',
          border: '1px solid #ddd',
          padding: '3px',
          borderRadius: '3px',
          marginTop: '10px'
        }}>
          <div style={{
            height: '20px',
            backgroundColor: '#667eea',
            width: `${progress}%`,
            borderRadius: '2px',
            textAlign: 'center',
            lineHeight: '20px',
            color: 'white'
          }}>
            {progress}%
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          marginTop: '25px'
        }}>
          <button
            onClick={uploadConfiguration}
            disabled={(!device || (!selectedFile && !jsonConfig)) || isUploading}
            style={{
              backgroundColor: (!device || (!selectedFile && !jsonConfig)) || isUploading ? '#ccc' : '#848b92',
              color: 'white',
              fontSize: '16px',
              padding: '12px 25px',
              border: 'none',
              borderRadius: '8px',
              cursor: (!device || (!selectedFile && !jsonConfig)) || isUploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
              fontWeight: '600'
            }}
          >
            {isUploading ? 'Uploading...' : `Upload ${settingtype ? settingtype.toUpperCase() : 'MQTT'} Config`}
          </button>
        </div>

        <div style={{
          marginTop: '20px',
          maxHeight: '150px',
          overflowY: 'auto',
          textAlign: 'left',
          fontSize: '13px',
          color: '#666',
          background: '#f8f8f8',
          padding: '10px',
          borderRadius: '6px'
        }}>
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublishMQTTSetting;